import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useEditorData } from '../../lib/FabricJs';
import { IFRAME_WIDGETS } from '../../config';
import { editorTheme, TOOLBAR_HEIGHT } from '../../theme';
import ColorPicker from '../Widgets/ColorPicker';
import Group from '../Group';
import LayerOrder from './LayerOrder';
import AlignDistribute from './AlignDistribute';
import FlipRotate from './FlipRotate';
import PositionSize from './PositionSize';
import QrCodeSettings from '../QrCode/QrCodeSettings';
import { isQrCode } from '../../lib/FabricJs/utils/qrObject';
import Section from './Section';

const PANEL_WIDTH = 288;

const Panel = styled.aside`
  width: ${PANEL_WIDTH}px;
  flex: none;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: ${editorTheme.surface};
  border-left: 1px solid ${editorTheme.border};
`;

const Title = styled.div`
  height: ${TOOLBAR_HEIGHT}px;
  flex: none;
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${editorTheme.text};
  border-bottom: 1px solid ${editorTheme.border};
`;

const TabList = styled.div`
  display: flex;
  flex: none;
  padding: 0 8px;
  border-bottom: 1px solid ${editorTheme.border};
`;

const TabButton = styled.button<{ selected: boolean }>`
  flex: 1;
  height: 40px;
  background: none;
  font-size: 13px;
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  color: ${({ selected }) =>
    selected ? editorTheme.accentHover : editorTheme.textMuted};
  border-bottom: 2px solid
    ${({ selected }) => (selected ? editorTheme.accent : 'transparent')};
  border-radius: 0;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

enum PropertiesTab {
  Design = 'Design',
  Arrange = 'Arrange',
}

const TEXT_TYPES = ['i-text', 'textbox', 'text'];

const getSelectionLabel = (activeObjects: fabric.Object[]) => {
  if (!activeObjects.length) return 'Slide';
  if (activeObjects.length > 1) return `${activeObjects.length} objects`;
  const [object] = activeObjects;
  switch (object.data?.type) {
    case IFRAME_WIDGETS.WORLD_CLOCK:
      return 'Clock';
    case IFRAME_WIDGETS.DATE:
      return 'Date';
    case IFRAME_WIDGETS.IFRAME:
      return 'Web widget';
    case 'kvs':
      return 'Dynamic text';
    case 'qr':
      return 'QR code';
    case 'video':
      return 'Video';
  }
  if (object.type === 'group') return 'Group';
  if (object.type === 'image') return 'Image';
  if (TEXT_TYPES.includes(object.type || '')) return 'Text';
  return 'Shape';
};

const PropertiesPanel: FC = () => {
  const { canvas } = useEditorData();
  const [tab, setTab] = useState(PropertiesTab.Design);
  const [, setSelectionVersion] = useState(0);

  // Selecting another object of the same kind changes no editor state, but
  // the panel still has to describe the new selection.
  useEffect(() => {
    if (!canvas) return;
    const refresh = () => setSelectionVersion((version) => version + 1);
    const events = [
      'selection:created',
      'selection:updated',
      'selection:cleared',
    ];
    events.forEach((name) => canvas.on(name, refresh));
    return () => events.forEach((name) => canvas.off(name, refresh));
  }, [canvas]);

  const activeObjects = canvas?.getActiveObjects() || [];
  const hasSelection = activeObjects.length > 0;

  return (
    <Panel aria-label="Properties">
      <Title>{getSelectionLabel(activeObjects)}</Title>
      {hasSelection && (
        <TabList role="tablist">
          {Object.values(PropertiesTab).map((value) => (
            <TabButton
              key={value}
              role="tab"
              aria-selected={tab === value}
              selected={tab === value}
              onClick={() => setTab(value)}
            >
              {value}
            </TabButton>
          ))}
        </TabList>
      )}
      <Body>
        {!hasSelection && (
          <Section title="Background">
            <ColorPicker />
          </Section>
        )}
        {hasSelection && tab === PropertiesTab.Design && (
          <>
            <PositionSize />
            {activeObjects.length === 1 && isQrCode(activeObjects[0]) && (
              <QrCodeSettings object={activeObjects[0]} />
            )}
            <Section title="Color">
              <ColorPicker />
            </Section>
          </>
        )}
        {hasSelection && tab === PropertiesTab.Arrange && (
          <>
            <LayerOrder />
            <AlignDistribute />
            <FlipRotate />
            <Group />
          </>
        )}
      </Body>
    </Panel>
  );
};

export default PropertiesPanel;
